import { QRCodeCanvas } from "qrcode.react";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { push, replace } from "connected-react-router";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  setUpTOTP,
  verifyTOTPSetup,
  getCurrentUser,
  updateMFAPreference,
  signOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  FormRow,
  FormRowItem,
  OTPInput,
  Toast,
} from "@tphglobal/components";
import { Box, Skeleton, Button as MuiButton } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import configAmplify from "../../config/awsConfig";
import { RegionTypes } from "@tphglobal/common/utils/constants";
import { logout, updateToken } from "../../redux/actions";
import { routes } from "../../utils";
import { UPDATE_MFA_STATUS_UK, UPDATE_MFA_STATUS_AU } from "../../api";
import { apiCall } from "@tphglobal/common/redux/actions";
import { FixedTitle, HttpMethods } from "@tphglobal/common/utils";
import ErrorHandler from "../../utils/errorHandler";
import { Container } from "../../components";
import {
  StyledContent,
  StyledContentContainer,
  StyledFormDetails,
  StyledFormHeading,
  StyledHeaderContainer,
  StyledInfoContainer,
  StyledLogo,
  StyledScreenWrapperMFA,
  StyledFormContainerMFA,
  StyledSmallLogo,
} from "./styles";
import useWindowSize from "@tphglobal/common/hooks/windowSize";
import { breakpoints } from "@tphglobal/common/theme/style.layout";
import { colors, greyScaleColour } from "@tphglobal/common/theme/style.palette";
import SidePanel from "./sidePanel";
import { ReduxState } from "../../redux/reducers";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getMFAStateFromToken } from "../../utils/mfaUtils";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

const SetupMFA = () => {
  const { width } = useWindowSize();
  const isMobile = width <= breakpoints.mobile;

  const [totpUri, setTotpUri] = useState("");
  const [sharedSecret, setSharedSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [setupError, setSetupError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const now = new Date();
  const token = useSelector((state: ReduxState) => state.auth.token);

  const { mfaEnabled } = token
    ? getMFAStateFromToken()
    : { mfaEnabled: false };
  const [hasAttemptedVerify, setHasAttemptedVerify] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const reduxDispatch = useDispatch();
  const selectedRegion = localStorage.getItem("region") as RegionTypes;

  const updateMFAStatus = async () =>
    new Promise<any>((resolve, reject) => {
      try {
        const apiUrl =
          selectedRegion === RegionTypes.AU
            ? UPDATE_MFA_STATUS_AU
            : UPDATE_MFA_STATUS_UK;

        reduxDispatch(
          apiCall(
            apiUrl,
            resolve,
            (err) => {
              toast(<Toast type="error" text={ErrorHandler(err?.message)} />);
            },
            HttpMethods.PUT,
            null,
          ),
        );
      } catch (error) {
        reject(error);
        toast(
          <Toast
            type="error"
            text={`Something went wrong while updating MFA status. Please try again.`}
          />,
        );
      }
    });

  const setupTotp = async () => {
    try {
      configAmplify(
        selectedRegion === RegionTypes.AU ? RegionTypes.AU : RegionTypes.UK,
      );
      const user = await getCurrentUser();

      const totpSetupDetails = await setUpTOTP();
      const secret = totpSetupDetails.sharedSecret;
      const envPrefix = encodeURIComponent("TPH-Freelancer-Portal");
      const uri = `otpauth://totp/${encodeURIComponent(
        user?.signInDetails?.loginId || "user",
      )}?secret=${secret}&issuer=${envPrefix}`;

      setTotpUri(uri);
      setSharedSecret(secret);
    } catch (error) {
      console.error("Error setting up TOTP:", error);
      // Only show error AFTER initial load
      if (!isInitialLoad) {
        setSetupError("Failed to setup MFA. Please try again.");
        toast(
          <Toast type="error" text="Failed to setup MFA. Please try again." />
        );
      }
    }
  };

  const handleCopySecret = async () => {
    if (!sharedSecret) return;

    try {
      await navigator.clipboard.writeText(sharedSecret);
      toast(<Toast type="success" text="Code copied to clipboard" />);
    } catch (err) {
      toast(<Toast type="error" text="Failed to copy code" />);
    }
  };


  const verifyTotp = async () => {
    setHasAttemptedVerify(true);
    if (!verificationCode.trim()) {
      setSetupError("Please enter a verification code");
      return;
    }

    setIsVerifying(true);
    setSetupError("");

    try {
      configAmplify(
        selectedRegion === RegionTypes.AU ? RegionTypes.AU : RegionTypes.UK,
      );
      // Verify the TOTP setup with the user-provided code
      await verifyTOTPSetup({ code: verificationCode.trim() });

      await updateMFAPreference({ totp: "PREFERRED" });
      await updateMFAStatus();

      // Refresh the auth session to get updated token with MFA status
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.idToken?.toString();

      if (newToken) {
        reduxDispatch(updateToken(newToken));
      }

      toast(<Toast type="success" text="MFA setup completed successfully!" />);

      // Navigate to home after a short delay
      setTimeout(() => {
        reduxDispatch(push(routes.home.root));
      }, 2000);
    } catch (error) {
      console.error("Error verifying TOTP:", error);
      setSetupError("Invalid verification code. Please try again.");
      toast(
        <Toast
          type="error"
          text="Invalid verification code. Please try again."
        />,
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerificationCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6); // Only allow 6 digits
    setVerificationCode(value);
    setSetupError(""); // Clear error when user starts typing
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("mfaSkipped");
      configAmplify(
        selectedRegion === RegionTypes.AU ? RegionTypes.AU : RegionTypes.UK,
      );
      try {
        await signOut();
      } catch (err) {
        console.log("SignOut failed, forcing logout", err);
      }

      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("CognitoIdentityServiceProvider")) {
          localStorage.removeItem(key);
        }
      });
      reduxDispatch(logout());
      reduxDispatch(push(routes.login));
      toast(<Toast type="success" text="Logged out successfully" />);
    } catch (error) {
      console.error("Error during logout:", error);
      toast(
        <Toast type="error" text="Error during logout. Please try again." />,
      );
    }
  };


  useEffect(() => {
    setupTotp().finally(() => {
      setIsInitialLoad(false);
    });
  }, []);

  return (
    <Container
      hideTabNavigation
      noBorderRadius
      hideSidebar
      noPadding
      hasHeader={false}
      noMargin
    >
      <StyledScreenWrapperMFA>
        <StyledFormContainerMFA>
          <StyledContentContainer>
            <StyledHeaderContainer>
              {isMobile ? (
                <StyledSmallLogo
                  src="/assets/images/smallLoginLogo.svg"
                  alt="smallLogo"
                />
              ) : (
                <StyledLogo src="/assets/images/loginLogo.svg" alt="logo" />
              )}
              <StyledInfoContainer>
                <StyledFormHeading>
                  Setup Multi-Factor Authentication (MFA)
                </StyledFormHeading>

                <FixedTitle
                  varient="Fixed14Regular"
                >
                  You are required to set up Multi-factor Authentication (MFA)
                  for additional security.
                </FixedTitle>
                <FixedTitle
                  varient="Fixed14Regular"
                style={{ marginBottom: "10px" }}
                >
                  This just means that even if someone gets your password, they still can’t get into your account.
                </FixedTitle>

                <FixedTitle varient="Fixed14Regular">
                  You can do this in 3 easy steps:
                  <br />
                  Step 1: Install an authenticator app
                  <br />
                  Step 2: Scan the QR code
                  <br />
                  Step 3: Enter the 6 digit code from the app... <strong>easy!</strong>
                </FixedTitle>
              </StyledInfoContainer>
            </StyledHeaderContainer>
            <StyledContent>
              <StyledFormDetails>
                <Form>
                  <FixedTitle varient="Fixed14Regular">
                    <strong>Step 1:</strong> Install an authenticator app.
                    <br />
                    <a
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: colors.information ?? "#1976d2",
                        textDecoration: "underline",
                      }}
                    >
                      Google Authenticator
                    </a>{" "}
                    or{" "}
                    <a
                      href="https://www.microsoft.com/en-au/security/mobile-authenticator-app"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: colors.information ?? "#1976d2",
                        textDecoration: "underline",
                      }}
                    >
                      Microsoft Authenticator
                    </a>{" "}
                    are both great options
                  </FixedTitle>

                  <FixedTitle varient="Fixed14Regular" style={{ marginTop: "20px" }}>
                    <strong>Step 2:</strong> Open the authenticator app, press the + or add button and scan the code below
                  </FixedTitle>
                  <FormRow marginTop="20px">
                    <FormRowItem justifyContent={"center"}>
                      {totpUri ? (
                        <QRCodeCanvas value={totpUri} />
                      ) : (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            width: 128,
                            height: 128,
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={120}
                            height={120}
                            sx={{ borderRadius: "4px" }}
                          />
                        </Box>
                      )}
                    </FormRowItem>
                  </FormRow>
                  <FixedTitle
                    varient="Fixed14Regular"
                  >
                    or manually copy this code into your existing authentication app:
                  </FixedTitle>
                  <Box
                    sx={{
                      position: "relative",
                      marginBottom: "20px",
                    }}
                  >
                    <code
                      style={{
                        backgroundColor: "#f5f5f5",
                        padding: "10px 40px 10px 10px",
                        borderRadius: "4px",
                        display: "block",
                        fontSize: "14px",
                        wordBreak: "break-all",
                        textAlign: "center",
                      }}
                    >
                      {sharedSecret}
                    </code>

                    <ContentCopyIcon
                      onClick={handleCopySecret}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        fontSize: "18px",
                        color: colors.information,
                      }}
                    />
                  </Box>

                  <FixedTitle
                    varient="Fixed14Regular"
                  >
                    <strong>Step 3:</strong> Enter the 6 digit number from the authenticator app:
                  </FixedTitle>
                  <FormRow marginTop="10px">
                    <FormRowItem justifyContent={"center"}>
                      <OTPInput
                        otp={verificationCode}
                        setOtp={setVerificationCode}
                        error={hasAttemptedVerify ? setupError : ""}
                        onChange={handleVerificationCodeChange}
                      />
                    </FormRowItem>
                  </FormRow>
                  <FormRow marginBottom="0">
                    <FormRowItem>
                      <Button
                        label={"Verify & Enable MFA"}
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={verifyTotp}
                        disabled={isVerifying || verificationCode.length !== 6}
                      />
                    </FormRowItem>
                  </FormRow>

                  <FixedTitle varient="Fixed14Regular" style={{ marginTop: "20px" }}>
                    <strong>Step 4:</strong> Done! Next time you login you’ll go straight to step 3.
                    So don’t delete this from your authenticator app, as we’ll ask for a code
                    from the app on your phone next time.
                  </FixedTitle>
                  <FormRow marginTop="16px" marginBottom={0}>
                    <FormRowItem>
                      <Box
                        sx={{
                          paddingTop: "4px",
                          paddingBottom: "4px",
                          display: "flex !important",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "16px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            flexWrap: "nowrap",
                          }}
                        >
                          <InfoOutlinedIcon
                            sx={{
                              color: colors.information ?? "#1976d2",
                              fontSize: "19px",
                              marginTop: "2px",
                              flexShrink: 0,
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <FixedTitle varient="Fixed14Regular">
                              If you are re-registering for MFA, make sure to delete any old entries for this account from your
                              authenticator app to avoid confusion.
                            </FixedTitle>
                          </Box>
                        </Box>
                      </Box>

                    </FormRowItem>
                  </FormRow>
                  <FormRow marginTop="16px" marginBottom={0}>
                    <FormRowItem><Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                      }}
                    >
                      <PriorityHighIcon
                        sx={{
                          color: colors.danger ?? "#d32f2f",
                          fontSize: "19px",
                          marginTop: "2px",
                          flexShrink: 0,
                        }}
                      />
                      <FixedTitle
                        varient="Fixed14Regular"
                        color={colors.danger ?? "#d32f2f"}
                      >
                        If you cannot see the QR code or the setup fails, please logout and
                        clear your browser’s cache by following our guide{" "}
                        <a
                          href="https://tphglobal.atlassian.net/wiki/x/AQCSb"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: colors.information ?? "#1976d2",
                            textDecoration: "underline",
                            fontWeight: 500,
                          }}
                        >
                          here
                        </a>
                      </FixedTitle>
                      </Box>
                    </FormRowItem>
                  </FormRow>
                  <FormRow marginTop="20px" marginBottom={0}>
                    <FormRowItem justifyContent="center">
                      <MuiButton
                        startIcon={
                          <LogoutIcon
                            sx={{
                              fontSize: "18px",
                            }}
                          />
                        }
                        onClick={handleLogout}
                        sx={{
                          textTransform: "none",
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#ffffff",
                          backgroundColor: colors.danger ?? "#d32f2f",
                          padding: "12px 16px",
                          borderRadius: "8px",
                          minWidth: "120px",
                          marginBottom: "10px",
                          "&:hover": {
                            backgroundColor: "#b71c1c",
                          },
                        }}
                        variant="contained"
                        disableElevation
                      >
                        Logout
                      </MuiButton>
                    </FormRowItem>
                  </FormRow>
                </Form>
              </StyledFormDetails>
            </StyledContent>
          </StyledContentContainer>
        </StyledFormContainerMFA>
        <SidePanel />
      </StyledScreenWrapperMFA>
    </Container>
  );
};

export default SetupMFA;
